import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ProjectActivitiesConstants, UserType, WorkflowOrganisationAccessLevel } from "../../constants";
import { TaskConstants } from "../../constants/workflow/TaskConstants";
import { SelectData, TaskPanelData, WorkflowStepModel } from "../../models";
import {
  GetTaskBoardResponse,
  searchActivities,
  searchProjectOrganisations,
  SearchWorkflowTasksResponse,
} from "../../service/query";
import { searchAclEntries } from "../../service/query/QueryService.full";
import { BaseResponseWithSingle, ServiceError, Status } from "../../service/Shared";
import {
  createWorkflowTask,
  CreateWorkflowTaskResponse,
  updateWorkflowTask,
  UpdateWorkflowTaskResponse,
} from "../../service/workflow";
import { useAuth } from "../../useAuth";
import { hasWorkflowOrganisationAccessLevel, useIsLoadingWrapper } from "../../utils";
import { getActivityDashboardTabRoute } from "../../utils/routes";
import { dataGridMapFilterCriteria, Toast } from "../../widget";

// Note - We can't use context here as this component is used in multiple places
interface UseWorkflowTaskPanelProps {
  task?: SearchWorkflowTasksResponse["results"][0];
  currentStepUuid: string | undefined;
  workflow?: GetTaskBoardResponse["results"][number];
  workflowSteps?: WorkflowStepModel[];

  show: boolean;
  refresh: boolean;
  handleRefresh: () => void;

  onClose: () => void;
}

interface UseWorkflowTaskPanelReturnData {
  isCreateTask: boolean;
  isDealWorkflow: boolean;
  currentUserType: UserType;

  workflowOrganisationAccessLevel: WorkflowOrganisationAccessLevel | undefined;
  hasFullEditAccess: boolean;

  stepSelectData: SelectData;
  // NOTE - hiding this until phase 3 KAN-7325 - https://kanaearth.atlassian.net/browse/KAN-7325
  // phaseKeyDatesSelectData: SelectData;
  organisationsSelectData: SelectData;
  visibilityOrganisationsSelectData: SelectData;
  activitySelectData: SelectData;

  errors: ServiceError[] | undefined;
  setErrors: Dispatch<SetStateAction<ServiceError[] | undefined>>;

  currentTask: TaskPanelData;
  setCurrentTask: Dispatch<SetStateAction<TaskPanelData>>;

  isTitleEditable: boolean;
  setIsTitleEditable: Dispatch<SetStateAction<boolean>>;

  showDeleteTaskModal: boolean;
  setShowDeleteTaskModal: Dispatch<SetStateAction<boolean>>;

  showCompletePhaseModal: boolean;
  setShowCompletePhaseModal: Dispatch<SetStateAction<boolean>>;

  updateStepsSelectDataByPhase: (phase: string) => void;
  handleLinkedActivityOnClick: () => void;
  handleCloseTaskPanel: () => void;
  handleSubmit: () => Promise<void>;
  isHandleSubmitLoading: boolean;
  defaultSelectedOrgTypeVisibility: string | undefined;
}

export const useWorkflowTaskPanel = ({
  task,
  currentStepUuid,
  workflow,
  show,
  refresh,
  handleRefresh,

  onClose,
}: UseWorkflowTaskPanelProps): UseWorkflowTaskPanelReturnData => {
  const navigate = useNavigate();
  const { currentOrganisationUuid, currentUserType } = useAuth();
  const [errors, setErrors] = useState<ServiceError[]>();
  const [isTitleEditable, setIsTitleEditable] = useState(false);

  const [stepSelectData, setStepSelectData] = useState<SelectData>([]);
  const [activitySelectData, setActivitySelectData] = useState<SelectData>([]);
  const [organisationsSelectData, setOrganisationsSelectData] = useState<SelectData>([]);
  const [visibilityOrganisationsSelectData, setVisibilityOrganisationsSelectData] = useState<SelectData>([]);
  // NOTE - hiding this for now KAN-7325 - https://kanaearth.atlassian.net/browse/KAN-7325
  // const [phaseKeyDatesSelectData, setPhaseKeyDatesSelectData] = useState<SelectData>([]);

  const [defaultSelectedOrgTypeVisibility, setDefaultSelectedOrgTypeVisibility] = useState<string>();

  const [currentTask, setCurrentTask] = useState<TaskPanelData>(
    task === undefined
      ? TaskConstants.DEFAULT_TASK
      : {
          deadlineDate: task.deadlineDate,
          isMandatory: task.isMandatory,
          isMilestone: task.isMilestone,
          keyDate: task.keyDate,
          status: task.status,
          repeat: task.repeat,
          description: task.description,
          name: task.name,
          visibility: [],
          assignedToUserUuid: task.assignee?.uuid ?? null,
          assignedToUserName: task.assignee?.name ?? null,
          ownerOrganisationUuid: task.ownerOrganisation.uuid,
          relateToActivity: task.objectType === "Activity",
          activityUuid: task.objectType === "Activity" ? task.objectUuid : null,
          stepUuid: task.step.uuid,
          phase: task.step.phase,
          uuid: task.uuid,
          rowVersion: task.rowVersion,
          resolution: task.resolution,
        }
  );

  const [workflowOrganisationAccessLevel, setWorkflowOrganisationAccessLevel] =
    useState<WorkflowOrganisationAccessLevel>();
  const [createdByCurrentUserOrg, setCreatedByCurrentUserOrg] = useState(false);
  const [isHandleSubmitLoading, setIsHandleSubmitLoading] = useState(false);
  const [showCompletePhaseModal, setShowCompletePhaseModal] = useState(false);
  const [showDeleteTaskModal, setShowDeleteTaskModal] = useState(false);

  const isDealWorkflow = useMemo(() => workflow?.dealUuid !== null, [workflow]);

  const getTaskVisibility = (): string[] => {
    if (isDealWorkflow && currentOrganisationUuid) {
      return [currentOrganisationUuid];
    }

    return currentTask.visibility ?? [];
  };

  const resetTemplateTaskPanel = (): void => {
    setIsTitleEditable(false);
    setCreatedByCurrentUserOrg(false);
    setShowCompletePhaseModal(false);
    setShowDeleteTaskModal(false);
    setStepSelectData([]);
    setCurrentTask(TaskConstants.DEFAULT_TASK);
    setErrors([]);
  };

  const handleCloseTaskPanel = (): void => {
    resetTemplateTaskPanel();
    onClose();
  };

  const handleLinkedActivityOnClick = (): void => {
    if (currentTask.activityUuid)
      navigate(getActivityDashboardTabRoute(currentTask.activityUuid, "tasks", currentUserType));
  };

  const handleSubmit = useIsLoadingWrapper(async (): Promise<void> => {
    setErrors([]);

    let res: BaseResponseWithSingle<UpdateWorkflowTaskResponse | CreateWorkflowTaskResponse>;
    if (task) {
      if (
        currentTask.status === TaskConstants.TASK_STATUS_COMPLETE &&
        (currentTask.resolution === null || currentTask.resolution.trim() === "")
      ) {
        setShowCompletePhaseModal(true);
        return;
      }
      res = await updateWorkflowTask({
        assignedToUserUuid: currentTask.assignedToUserUuid !== "unassigned" ? currentTask.assignedToUserUuid : null,
        deadlineDate: currentTask.deadlineDate,
        description: currentTask.description,
        isMandatory: currentTask.isMandatory ?? false,
        isMilestone: currentTask.isMilestone ?? false,
        keyDate: currentTask.keyDate,
        name: currentTask.name ?? "",
        objectType: currentTask.relateToActivity ? "Activity" : null,
        objectUuid: currentTask.activityUuid,
        reminderDate: null,
        reminderType: null,
        repeat: currentTask.repeat ?? false,
        visibility: getTaskVisibility(),
        ownerOrganisationUuid:
          isDealWorkflow && currentOrganisationUuid
            ? currentOrganisationUuid
            : (currentTask.ownerOrganisationUuid ?? ""),
        rowVersion: task.rowVersion,
        resolution: currentTask.status === TaskConstants.TASK_STATUS_COMPLETE ? task.resolution : null,
        status: currentTask.status ?? "",
        workflowTaskUuid: task.uuid,
        workflowStepUuid: currentTask.stepUuid ?? "",
      });
    } else {
      res = await createWorkflowTask({
        assignedToUserUuid:
          currentOrganisationUuid === currentTask.ownerOrganisationUuid &&
          currentTask.assignedToUserUuid !== "unassigned"
            ? currentTask.assignedToUserUuid
            : null,
        deadlineDate: currentTask.deadlineDate,
        description: currentTask.description,
        isMandatory: currentTask.isMandatory ?? false,
        isMilestone: currentTask.isMilestone ?? false,
        keyDate: currentTask.keyDate,
        name: currentTask.name ?? "",
        objectType: currentTask.relateToActivity ? "Activity" : null,
        objectUuid: currentTask.activityUuid,
        reminderDate: null,
        reminderType: null,
        repeat: currentTask.repeat ?? false,
        visibility: getTaskVisibility(),
        ownerOrganisationUuid:
          isDealWorkflow && currentOrganisationUuid
            ? currentOrganisationUuid
            : (currentTask.ownerOrganisationUuid ?? ""),
        workflowStepUuid: currentTask.stepUuid ?? "",
        status: currentTask.status ?? "Unassigned",
      });
    }
    if (res.status === Status.Success && res.data) {
      Toast.success({
        message: `Workflow task ${task ? "updated" : "created"} successfully`,
      });
      handleRefresh();
      handleCloseTaskPanel();
    }

    if (res.status === Status.Error) {
      setErrors(res.errors);
    }
  }, setIsHandleSubmitLoading);

  const getAndSetActivityList = async (): Promise<void> => {
    if (workflow) {
      // Always filter by project
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const filter: any = {
        status: {
          operator: "in",
          value: [ProjectActivitiesConstants.STATUS_IN_PROGRESS],
        },
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let filterResults: any = {
        project: {
          uuid: {
            operator: "eq",
            value: workflow?.projectUuid,
          },
        },
      };

      // If in group, filter by group
      if (workflow.groupUuid) {
        filterResults = {
          ...filterResults,
          group: {
            uuid: {
              operator: "eq",
              value: workflow.groupUuid || null,
            },
          },
        };
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const filterGroups: any = [
        {
          operator: "or",
          filter: {
            results: filterResults,
          },
        },
      ];

      const response = await searchActivities({
        paging: {
          limit: 10,
          beforeCursor: "",
          afterCursor: "",
        },
        filter: {
          results: filter,
        },
        filterGroups,
      });
      if (response.status === Status.Success && response.data?.results) {
        const list = response.data.results.flatMap((el) => {
          if (el.uuid && el.activityDefinition.displayName) {
            return { key: el.uuid, value: el.activityDefinition.displayName };
          }
          return [];
        });

        setActivitySelectData(list);
      }
    }
  };

  const getAndSetDefaultSelectedOrgTypeVisibility = async (): Promise<void> => {
    if (workflow) {
      const filterCriteria = dataGridMapFilterCriteria([]);

      filterCriteria.results = {
        role: {
          operator: "in",
          value: "Asset Manager, Developer, Development Manager, Guest - Write",
        },
      };

      const response = await searchProjectOrganisations({
        projectUuid: workflow.projectUuid,
        paging: { beforeCursor: null, afterCursor: null, limit: 50 },
        filter: filterCriteria,
      });

      if (response.status === Status.Success && response.data) {
        const selectData: SelectData = response.data.results
          .filter((org) => org.displayName)
          .map((org) => ({ key: org.organisationUuid, value: org.displayName as string }));
        setVisibilityOrganisationsSelectData(selectData);

        const currentOrg = response.data.results.find((f) => f.organisationUuid === currentOrganisationUuid);
        const defaultUuid = currentOrg?.organisationUuid;
        setDefaultSelectedOrgTypeVisibility(defaultUuid);
        if (!task && defaultUuid) {
          setCurrentTask((prev) => ({ ...prev, visibility: [defaultUuid] }));
        }
      }
    }
  };

  const getAndSetVisibilityFromAcl = async (): Promise<void> => {
    if (!task) return;

    const response = await searchAclEntries({ resourceUuid: task.uuid });
    if (response.status === Status.Success && response.data) {
      const selectedUuids = response.data
        .filter((entry) => entry.permissions.includes("readWorkflowTask"))
        .map((entry) => entry.subjectUuid);

      if (currentOrganisationUuid && !selectedUuids.includes(currentOrganisationUuid)) {
        selectedUuids.push(currentOrganisationUuid);
      }

      setCurrentTask((prev) => ({ ...prev, visibility: selectedUuids }));
    }
  };

  const updateStepsSelectDataByPhase = (phase: string): void => {
    if (workflow?.steps) {
      setStepSelectData(
        workflow.steps
          .filter((step) => step.phase === phase)
          .map((step) => ({ key: step.uuid, value: step.name })) as SelectData
      );
    }
  };

  // Checks if user can edit the opened task OR
  // if creating new task, has Contributor permission
  useEffect(() => {
    setCreatedByCurrentUserOrg(task?.createdByOrganisation.uuid === currentOrganisationUuid);
  }, [task?.createdByOrganisation.uuid]);

  // Updates current step phase & phase key dates for dropdown
  useEffect(() => {
    const phase = currentTask.stepUuid
      ? workflow?.steps.find((step) => step.uuid === currentTask?.stepUuid)?.phase
      : task?.step.phase;

    setCurrentTask({ ...currentTask, phase: phase ?? null });

    if (phase) {
      updateStepsSelectDataByPhase(phase);
    }
    // NOTE - hiding this for now KAN-7325 - https://kanaearth.atlassian.net/browse/KAN-7325
    // setPhaseKeyDatesSelectData(TemplateTaskConstants.PHASE_KEY_DATES_SELECT_DATA_MAP[phase]);
  }, [task?.step.phase]);

  useEffect(() => {
    if (show) {
      const currentPhase = workflow?.steps.find((step) => step.uuid === currentStepUuid)?.phase;
      if (currentPhase) updateStepsSelectDataByPhase(currentPhase);
      setCurrentTask(
        task === undefined
          ? {
              ...TaskConstants.DEFAULT_TASK,
              ownerOrganisationUuid: isDealWorkflow ? (currentOrganisationUuid ?? null) : null,
              stepUuid: currentStepUuid ?? null,
              phase: currentPhase !== undefined ? currentPhase : null,
            }
          : {
              deadlineDate: task.deadlineDate,
              isMandatory: task.isMandatory,
              isMilestone: task.isMilestone,
              keyDate: task.keyDate,
              status: task.status,
              repeat: task.repeat,
              description: task.description,
              name: task.name,
              visibility: [],
              assignedToUserUuid: task.assignee?.uuid ?? null,
              assignedToUserName: task.assignee?.name ?? null,
              ownerOrganisationUuid: task.ownerOrganisation.uuid,
              relateToActivity: task.objectType === "Activity",
              activityUuid: task.objectType === "Activity" ? task.objectUuid : null,
              stepUuid: task.step.uuid,
              phase: task.step.phase,
              uuid: task.uuid,
              rowVersion: task.rowVersion,
              resolution: task.resolution,
            }
      );
    }
  }, [currentOrganisationUuid, currentStepUuid, isDealWorkflow, show, task, workflow?.steps]);

  // Updates step select data from workflow tamplate
  useEffect(() => {
    if (currentTask.phase !== null) {
      updateStepsSelectDataByPhase(currentTask.phase);
    }
    if (workflow?.organisations && workflow?.organisations.length > 0) {
      setOrganisationsSelectData(
        workflow?.organisations.map((organisation) => ({
          key: organisation.organisationUuid,
          value: organisation.name,
        })) as SelectData
      );
      setWorkflowOrganisationAccessLevel(
        workflow.organisations.find((x) => x.organisationUuid === currentOrganisationUuid)
          ?.accessLevel as WorkflowOrganisationAccessLevel
      );
    }
    if (workflow?.projectUuid) {
      getAndSetActivityList();
      getAndSetDefaultSelectedOrgTypeVisibility();
    }
  }, [workflow?.uuid, refresh]);

  useEffect(() => {
    if (show && workflow?.projectUuid) {
      getAndSetDefaultSelectedOrgTypeVisibility();
      getAndSetVisibilityFromAcl();
    }
  }, [show, task?.uuid]);

  return {
    isCreateTask: task === undefined,
    isDealWorkflow,
    currentUserType,

    // phaseKeyDatesSelectData,
    stepSelectData,
    organisationsSelectData,
    visibilityOrganisationsSelectData,
    activitySelectData,

    errors,
    setErrors,

    currentTask,
    setCurrentTask,

    isTitleEditable,
    setIsTitleEditable,

    showDeleteTaskModal,
    setShowDeleteTaskModal,

    showCompletePhaseModal,
    setShowCompletePhaseModal,

    updateStepsSelectDataByPhase,
    handleLinkedActivityOnClick,
    handleCloseTaskPanel,
    handleSubmit,
    isHandleSubmitLoading,
    defaultSelectedOrgTypeVisibility,

    workflowOrganisationAccessLevel,
    hasFullEditAccess:
      task === undefined ||
      hasWorkflowOrganisationAccessLevel(WorkflowOrganisationAccessLevel.Author, workflowOrganisationAccessLevel) ||
      (hasWorkflowOrganisationAccessLevel(
        WorkflowOrganisationAccessLevel.Contributor,
        workflowOrganisationAccessLevel
      ) &&
        createdByCurrentUserOrg),
  };
};
